// 日本語(原文)。ほかの言語の辞書はこの型(SiteDict)に合わせる。
import type { RelativeDate } from "../../../utils/postMeta";

const ja = {
  site: {
    /** 下層ページの <title> に付けるサフィックス */
    titleSuffix: "!Legit - Reol非公式ファンサイト",
    /** トップページの <title> */
    topTitle: "!Legit｜Reolの楽曲・ライブ・セトリを網羅する非公式ファンサイト",
    defaultDescription: (years: number) =>
      `Reol(れをる)の非公式ファンサイト。全楽曲のライブ演奏統計、歴代ライブのセットリスト、MVロケ地(聖地)マップ、ファンタイプ診断まで。${years}年分の活動を横断検索できます。`,
    breadcrumbHome: "ホーム",
  },
  header: {
    search: "検索",
    searchTitle: "楽曲・LIVE・ロケ地を横断検索",
    toDark: "ダークモードに切替",
    toLight: "ライトモードに切替",
    myPage: "マイページ",
    menu: "メニュー",
    language: "言語",
  },
  footer: {
    officialSite: "公式サイト",
    goods: "グッズ",
    disclaimer:
      "本サイトはReol公式とは関係のない非公式ファンサイトです。楽曲・映像は公式の埋め込み/リンクのみ使用しています。",
    operator: "運営(非公式ファンサイト)",
    privacy: "プライバシーポリシー",
    // 翻訳版だけに出す機械翻訳の注記(日本語版では表示しない)
    mtNotice: "",
    mtContact: "",
  },
  video: {
    close: "動画を閉じる",
    play: "動画を再生",
    back: "動画の場所へ",
    playing: "右上で再生中",
    region: "再生中の動画",
  },
  home: {
    // ヒーローの見出し: 2行目の先頭は1行目の書き出しと揃えるための不可視の字下げ
    heroLine1Before: "Reolの",
    heroLine1Accent: "これまで",
    heroLine1After: "と、",
    heroLine2Indent: "Reolの",
    heroLine2Accent: "これから",
    heroLine2After: "を知る。",
    // ヒーロー見出しの各行の幅(em)。スマホでは2行目の字下げを外すため字下げ抜き、
    // 広い画面では字下げ込みの長い方の行。見出しの文字サイズを画面幅から逆算して
    // 1行に収めるために使う(文言を変えたら計測し直すこと)
    heroEmMobile: 9.2,
    heroEmWide: 11.2,
    heroLead1: "楽曲・ライブ・セトリ・ロケ地。れをる時代から現在まで、",
    heroLead2: "公式コンテンツへの案内板を兼ねた非公式アーカイブ。",
    ticketCta: "公式サイトでチケット →",
    // HOME の NEW RELEASE(plan/60)
    listenCta: "ディスコグラフィで見る →",
    welcomeTitle: "はじめてのReol",
    welcomeDesc: "代表曲と年代別ガイド。どのeraから入っても迷わない。",
    liveTitle: "ライブに行く",
    liveDesc: (n: number) => `${n}公演のセトリアーカイブと初参加ガイド。`,
    liveGuideLink: "初参加ガイドを読む →",
    dataTitle: "データを掘る",
    dataDesc: (n: number) => `${n}曲の演奏回数・初披露・最終演奏を全曲収録。`,
    timemachineTitle: "タイムマシン",
    timemachineDesc: "年月スライダーで、当時の楽曲数・ライブ数・演奏数を再現。",
    cgraphTitle: "相関図",
    cgraphDesc: "楽曲に関わったクリエイター同士のつながりを可視化。",
    heatmapTitle: "開催地マップ",
    heatmapDesc: "都道府県別の公演数を色分けしたヒートマップ。",
    sorterTitle: "楽曲ソーター",
    sorterDesc: "2曲ずつ勝ち抜き戦。あなたの一番好きな曲を決めるトーナメント。",
    grammarTitle: "セトリの文法",
    grammarDesc: "曲の隣接関係・定位置・年別傾向をデータで解析。",
    compareTitle: "セトリ比較",
    compareDesc: "2公演のセットリストを比較し、共通曲・差分・曲順の変化を可視化。",
    tourHeatmapTitle: "ツアーヒートマップ",
    tourHeatmapDesc: "1ツアーの全公演を横断し、楽曲ごとの演奏有無・曲順を一覧表示。",
    similarityTitle: "セトリ類似度ランキング",
    similarityDesc: "異なるツアー同士でセトリが特に似ている公演ペアをランキング。",
    postsTitle: "関連ポスト",
    postsDesc: "楽曲・ライブ・各公演の関連ポストを時系列で横断表示。",
    quizTitle: "Reolファンタイプ診断",
    quizDesc: "全20問の質問であなたのファンタイプを診断！",
    guideTitle: "ライヴ参戦ガイド",
    guideDesc: "持ち物・服装・マナーから遠征のコツまで",
  },
  explore: {
    kicker: "EXPLORE — 主要コンテンツ",
    discography: "リリースと全曲情報",
    live: "公演情報・セトリ",
    place: "ロケ地マップ",
    photo: "フォトギャラリー",
  },
  otd: {
    yesterday: "昨日",
    dayBeforeYesterday: "一昨日",
    daysAgo: (n: number) => `${n}日前`,
    tomorrow: "明日",
    dayAfterTomorrow: "明後日",
    daysLater: (n: number) => `${n}日後`,
    releaseSuffix: "リリース",
    releaseTitle: (title: string) => `『${title}』`,
    kickerMonth: "ON THIS MONTH — 今月の出来事",
    kickerDay: "ON THIS DAY — 今日は何の日",
    yearsAgoToday: (years: number) => `${years}年前の今日`,
    anniversaryIn: (when: string, years: number) => `${when}で${years}周年`,
    anniversaryWas: (when: string, years: number) => `${when}が${years}周年`,
    monthEntry: (md: string, years: number) => `${md}（${years}年前）`,
    upcoming: (days: number, years: number) => `あと${days}日で${years}周年`,
    seeAll: "この日の全記録を見る →",
  },
  common: {
    postTiming: (kind: "live" | "release", rel: RelativeDate): string => {
      const ref = kind === "live" ? "公演" : "リリース";
      if (rel.unit === "same") return `${ref}当日`;
      if (kind === "live" && rel.unit === "day" && rel.amount === 1) return rel.direction === "before" ? "公演前日" : "公演翌日";
      const unit = rel.unit === "day" ? "日" : rel.unit === "month" ? "ヶ月" : "年";
      return `${ref}${rel.amount}${unit}${rel.direction === "before" ? "前" : "後"}`;
    },
    postCategory: { 本人: "本人", 公式: "公式", メディア: "メディア" } as Record<string, string>,
    showMore: (n: number) => `もっと見る (${n}件)`,
    backToTop: "ページの先頭へ戻る",
    backHome: "HOMEへ戻る",
    loading: "読み込み中...",
    loadError: (err: string) => `読み込みに失敗しました: ${err}`,
  },
  discography: {
    intro: "DISCOGRAPHYではこれまでのリリース情報や歌ってみた動画などの一覧を時間軸で掲載しています。",
    metaDescription:
      "Reol のこれまでのリリース情報や歌ってみた動画などを時間軸で掲載。各楽曲のリンクや楽曲解析情報も確認できます。",
    collectionLabel: "コレクション(所有/視聴済み)",
    collectionHint: "各カードの「✓」で所有/視聴済みを記録できます(この端末のブラウザ内のみに保存)",
    count: (n: number) => `${n}件`,
    unknownYear: "不明",
    helpFilter: "上部に表示されたハッシュタグを押すと一覧を簡易的にフィルタすることができます",
    helpFilterExample: "例）「#れをる」を押下すると「れをる」名義の情報のみが表示されます",
    helpPlayBefore: "タイトル左の",
    helpPlayAfter: "をタップすると画面上部のプレイヤーで動画を再生します",
    helpOpenBefore: "「",
    helpOpenAfter: "開く」を押すと収録曲一覧や楽曲ごとの各種リンクが表示されます",
    helpCommentBefore: "曲名の右隣に「",
    helpCommentAfter: "」が表示されている場合、クリックするとSpotifyのリンクや楽曲の解析情報が表示されます",
    detail: "詳細",
    detailTitle: "楽曲詳細ページ(演奏統計・MV・関連ポスト)を見る",
    markOwned: "所有/視聴済みにする",
    unmarkOwned: "所有済みから外す",
    owned: "所有済み",
    ownedCount: (n: number) => `${n}人が所有/視聴済み`,
    tracks: "収録曲",
    copied: "コピーしました",
    copyTitles: "曲名をコピー",
    interview: "インタビュー",
    relatedPosts: "関連ポスト",
  },
  /** シート由来のフォーマット名の表示。日本語はそのまま */
  formats: {} as Record<string, string>,
  live: {
    metaDescription:
      "Reol が過去に出演したワンマンライヴ・ツアー・フェスなどの情報。各ライヴごとのセトリ、レポート、関連ポストを掲載しています。",
    intro1: "LIVEでは過去に出演したワンマンライヴやツアー、フェスなどの情報を掲載しています。",
    intro2:
      "ライヴごとのセトリや関連ポスト、ライヴレポートなどを載せていますので、参加できなかったライヴもどんな雰囲気だったのか少しでも感じ取れる様な情報を掲載しています。",
    attendanceLabel: "参戦記録",
    attendanceHint: "各公演カードの「✓」で参戦済みを記録できます(この端末のブラウザ内のみに保存)",
    heardLabel: "生で聴いた曲",
    heardHint: "参戦済みにした公演のセットリストから集計しています(ライブで演奏されたことのある曲が対象)",
    heardList: (n: number) => `聴いた曲 (${n})`,
    unheardList: (n: number) => `まだ生で聴いていない曲 (${n}・演奏回数の多い順)`,
    ongoing: "開催中",
    upcoming: "直近の開催予定",
    helpFilter: "上部に表示されたハッシュタグを押すと一覧を簡易的にフィルタすることができます",
    helpFilterExample: "例）「#れをる」を押下すると「れをる」名義の情報のみが表示されます",
    helpDetailBefore: "「",
    helpDetailAfter: " 詳細」を押すとライヴレポートや開催場所、セトリ、関連ポストなどの情報が表示されます",
    tagEvent: "イベント出演",
    tagOneman: "ワンマンライヴ",
    count: (n: number) => `${n}件`,
    unknownYear: "不明",
    setlistCopyTitle: (title: string) => `${title} セットリスト`,
    markAttended: "参戦済みにする",
    unmarkAttended: "参戦済みから外す",
    attended: "参戦済み",
    attendedInYear: (n: number) => `参戦 ${n}回`,
    showCount: (n: number) => `${n}公演`,
    songCount: (n: number) => `${n}曲`,
    detailTitle: "この公演の詳細ページ(セットリスト・会場・関連ポスト)を見る",
    detail: "詳細",
    youtubePlaylist: "YouTubeプレイリスト",
    attendedCount: (n: number) => `${n}人が参戦済み`,
    setlist: "セットリスト",
    copied: "コピーしました",
    copy: "コピー",
    songPageTitle: "楽曲詳細ページを見る",
    songStatsTitle: "楽曲統計ページで演奏履歴を見る",
    noSetlist: "セットリスト情報は現在登録されていません",
    toShowPage: "公演詳細ページを見る",
    spoilerBadgeTitle: "開催中のツアー。セットリストはネタバレ注意",
    spoilerBadge: "⚠ ネタバレ注意",
    expand: "展開",
    spoilerNoticeTitle: "⚠ ネタバレ注意",
    spoilerNotice1: "このツアーは現在開催中です。",
    spoilerNotice2: "セットリストや関連投稿にはネタバレが含まれます。",
    spoilerNotice3: "内容を確認した上でご覧ください。",
    showSpoiler: "ネタバレを承知の上で表示する",
    relatedPosts: "関連ポスト",
  },
  song: {
    shareText: (song: string) => `Reol「${song}」の演奏統計・MV・配信リンク | !Legit(非公式ファンサイト)`,
    totalPlays: "通算演奏",
    playsValue: (n: string) => `${n}回`,
    firstPlayed: "初披露",
    lastPlayed: "最終演奏",
    album: "収録アルバム:",
    lyrics: "作詞",
    music: "作曲",
    stream: "配信で聴く",
    lyricsLink: "歌詞を見る(歌ネット)",
    watchMv: "公式MVを見る",
    articles: "この曲の読みもの",
    shareTitle: "この曲のページをXでシェア",
    share: "Xでシェア",
    historyKicker: (n: string) => `PERFORMANCE HISTORY — ${n}件`,
    noHistoryTitle: "ライブ演奏の記録はまだありません",
    noHistoryDesc: "今後のライブで披露された際にはここに演奏履歴が追加されます。",
    venueTbd: "会場未定",
    closeVideo: "閉じる",
    watchVideo: "映像を見る",
    relatedPosts: "関連ポスト",
    toStatsTitle: "楽曲統計へ",
    toStatsDesc: "全曲の演奏回数・初披露・最終演奏をまとめて見る。",
    toSearchTitle: "横断検索へ",
    toSearchDesc: "楽曲・ライブ・ロケ地を横断してキーワード検索。",
    backToTop: "トップに戻る",
    metaLead: (song: string) => `Reol「${song}」のライブ演奏統計。`,
    metaPlays: (n: number, first: string | null) => `通算${n}回演奏、初披露は${first ?? "不明"}。`,
    metaNoPlays: "ライブでの演奏記録は確認されていません。",
    metaTail: "演奏履歴・公式MV・配信リンクを掲載。",
    breadcrumbStats: "楽曲統計",
  },
  show: {
    setlistCopyTitle: (title: string) => `${title} セットリスト`,
    shareText: (heading: string) => `Reol「${heading}」のセットリスト・会場情報 | !Legit(非公式ファンサイト)`,
    venue: "会場",
    venueSite: "会場サイトを見る",
    reliveTitle: "Relive Player (β) — このセットリストをローカル音源で再生します",
    relive: "Relive Player (β・要自前音源)",
    shareTitle: "この公演のページをXでシェア",
    share: "Xでシェア",
    mapTitle: (heading: string) => `会場マップ - ${heading}`,
    setlistKicker: (n: number) => `SETLIST — ${n}曲`,
    mcCount: (n: number) => `（MC ${n}）`,
    copied: "コピーしました",
    copy: "コピー",
    noSetlistTitle: "セットリスト情報は現在登録されていません",
    noSetlistDesc: "情報が確認でき次第、追加します。",
    closeVideo: "閉じる",
    watchVideo: "映像を見る",
    venueTbd: "会場未定",
    relatedPosts: "関連ポスト",
    toArchiveTitle: "LIVEアーカイブ一覧へ",
    toArchiveDesc: "歴代ライブのセットリスト・公演情報をまとめて見る。",
    toSearchTitle: "横断検索へ",
    toSearchDesc: "楽曲・ライブ・ロケ地を横断してキーワード検索。",
    backToTop: "トップに戻る",
    metaLead: (name: string) => `Reol「${name}」の公演情報。`,
    metaVenue: (place: string) => `会場: ${place}。`,
    metaSetlist: (n: number) => `セットリスト${n}曲を掲載。`,
  },
  stats: {
    title: "楽曲統計",
    metaDescription: "Reolの全楽曲のライブ演奏統計。通算演奏回数・初披露日・最終演奏日を一覧できます。",
    lead: "LIVEで演奏された楽曲の通算演奏回数・初出 / 最終演奏日を一覧します。",
    rulesLink: "集計ルールについて →",
    totalPlays: "演奏数 (述べ)",
    uniqueSongs: "ユニーク楽曲",
    searchPlaceholder: "🔍 曲名で検索",
    sortPlays: "演奏回数 (多)",
    sortLast: "最終演奏 (新)",
    sortFirst: "初演奏 (古)",
    sortName: "曲名 (あいうえお)",
    songCount: (n: string) => `${n} 曲`,
    toSongPageTitle: "楽曲詳細ページへ",
    firstPlayed: "初演奏",
    lastPlayed: "最新",
    toAlbum: "収録アルバムを見る →",
    toSongPage: "この曲のページへ →",
    watchMv: "▶ 公式MVを見る",
    stream: "♪ 配信で聴く",
    history: "演奏履歴",
    historyCount: (n: number) => `(${n}件)`,
    noMatch: "該当する楽曲が見つかりません",
    backToTop: "← トップに戻る",
    historyLoadError: "演奏履歴の読み込みに失敗しました",
    historyLoading: "演奏履歴を読み込み中...",
    historyEmpty: "演奏履歴がありません",
  },
};

export type SiteDict = typeof ja;
export default ja;
